import { FC, Fragment } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormInput } from '@microservices/wiskey-react-components'
import { Clear as ClearIcon } from '@mui/icons-material'
import { Box, IconButton, Typography } from '@mui/material'

import { CommandParameters } from '@components/CommandParameters'

import { useCommands } from '@hooks'
import { isObjectSource } from '@helpers'
import { GENERATOR_INPUT_TYPE, SOURCE_SELECTION_OPTIONS, TYPES_SOURCE_OPTIONS } from '@constants'
import { ActionJSScriptConfig, AutocompleteOption } from '@types'

type CommandFieldsItemProps = {
  objectCode: string
  objectTitle: string
  name: string
  typeName: string
  commandName: string
  isTypeHidden?: boolean
  isSingle?: boolean
  isAnotherObjectWithRequestData?: boolean
  canChangeRefObject?: boolean
  field: Record<'id', string>
  index: number
  onRemoveItem: (index: number) => void
  loadingObjects?: boolean
  loadingSystems?: boolean
  objectsOptions?: AutocompleteOption[]
  refObjectOptions?: AutocompleteOption[]
  systemsOptions?: AutocompleteOption[]
  typeOptions?: AutocompleteOption[]
  disableInputs: boolean
  isSelectedCommandOptionDisabled?: boolean
  showClearButton?: boolean
  jsScriptConfig?: ActionJSScriptConfig
}

export const CommandFieldsItem: FC<CommandFieldsItemProps> = ({
  objectCode,
  objectTitle,
  name,
  typeName,
  commandName,
  isTypeHidden,
  isSingle,
  isAnotherObjectWithRequestData,
  field,
  index,
  onRemoveItem,
  loadingObjects,
  loadingSystems,
  systemsOptions = [],
  objectsOptions = [],
  refObjectOptions = [],
  typeOptions,
  disableInputs,
  canChangeRefObject,
  isSelectedCommandOptionDisabled,
  showClearButton,
  jsScriptConfig,
}) => {
  const { t } = useTranslation()

  const { getValues, setValue, watch } = useFormContext()
  const values = getValues(name)

  const watchedSourceCommand = watch(`${name}.${index}.sourceCommand`)
  const watchedSourceType = watch(`${name}.${index}.sourceType`)
  const watchedCommandCode = watch(`${name}.${index}.${commandName}`)
  const watchedTypeCommand = watch(`${name}.${index}.${typeName}`)

  const { commandOptions, commandsData } = useCommands({
    sourceCommand: watchedSourceCommand?.id,
    currentSourceType: watchedSourceType?.label as TYPES_SOURCE_OPTIONS,
  })

  const sourceCommandOptions = isObjectSource(watchedSourceType?.label)
    ? objectsOptions
    : systemsOptions

  const getOptionDisabled = (option: AutocompleteOption, optionType: string) => {
    if (!isSelectedCommandOptionDisabled) {
      return false
    }

    return Boolean(
      values?.find(
        (command: Record<string, AutocompleteOption>) => command[optionType]?.id === option.id
      )
    )
  }

  const clearParamsAndBody = () => {
    setValue(`${name}.${index}.parameters`, [])
    setValue(`${name}.${index}.body`, [])
  }

  const handleChangeSourceType = (newSourceType: AutocompleteOption) => {
    if (newSourceType?.id === watchedSourceType?.id) return

    setValue(`${name}.${index}.sourceCommand`, null)
    setValue(`${name}.${index}.${commandName}`, null)

    clearParamsAndBody()
  }

  const handleChangeSourceCommand = (newSourceCommand: AutocompleteOption) => {
    if (newSourceCommand?.id === watchedSourceCommand?.id) return

    setValue(`${name}.${index}.${commandName}`, null)

    clearParamsAndBody()
  }

  const handleChangeCommandCode = (newCommandCode: AutocompleteOption) => {
    if (newCommandCode?.id !== watchedCommandCode?.id) clearParamsAndBody()

    if (commandsData?.length) {
      const command = commandsData.find(object => object?.code === newCommandCode?.id) ?? null
      setValue(`${name}.${index}.httpMethod`, command?.httpMethod)
    }
  }

  const handleClear = () => {
    onRemoveItem(index)
  }

  const label = isSingle
    ? t(`label.commandTitle`)
    : t(`label.commandTitleWithIndex`, { number: index + 1 })

  return (
    <Fragment key={field.id}>
      <Box display='flex'>
        <Typography my={0.5} variant='h6'>
          {label}
        </Typography>
        {showClearButton && (
          <IconButton size='small' onClick={handleClear}>
            <ClearIcon />
          </IconButton>
        )}
      </Box>
      {!isTypeHidden && (
        <FormInput
          autocompleteOptions={typeOptions}
          disabled={disableInputs}
          getOptionDisabled={option => getOptionDisabled(option, typeName)}
          inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
          label={t('label.commandType')}
          labelSx={{ minWidth: '150px' }}
          name={`${name}.${index}.${typeName}`}
          placeholder={t('placeholder.selectCommandType')}
          rules={{ required: true }}
        />
      )}
      <FormInput
        autocompleteOptions={SOURCE_SELECTION_OPTIONS}
        disableClearable={true}
        disabled={disableInputs || (!isTypeHidden && !watchedTypeCommand)}
        inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
        label={t('label.sourceType')}
        labelSx={{ minWidth: '150px' }}
        name={`${name}.${index}.sourceType`}
        placeholder={t('placeholder.selectSourceType')}
        rules={{ required: true }}
        onChangeAutocomplete={handleChangeSourceType}
      />
      {isAnotherObjectWithRequestData && (
        <FormInput
          autocompleteOptions={sourceCommandOptions}
          disabled={!watchedSourceType || (!isTypeHidden && !watchedTypeCommand)}
          inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
          label={t('label.sourceCommand')}
          labelSx={{ minWidth: '150px' }}
          loading={loadingObjects || loadingSystems}
          name={`${name}.${index}.sourceCommand`}
          placeholder={t('placeholder.selectSourceCommand')}
          rules={{ required: isAnotherObjectWithRequestData }}
          onChangeAutocomplete={handleChangeSourceCommand}
        />
      )}
      <Box display={'flex'}>
        <FormInput
          autocompleteOptions={commandOptions}
          disabled={isAnotherObjectWithRequestData ? !watchedSourceCommand?.id : !objectCode}
          getOptionDisabled={option => getOptionDisabled(option, commandName)}
          inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
          label={t('label.commandCode')}
          labelSx={{ minWidth: '150px' }}
          name={`${name}.${index}.${commandName}`}
          placeholder={t('placeholder.selectCommandCode')}
          rules={{ required: true }}
          onChangeAutocomplete={handleChangeCommandCode}
        />
        <FormInput
          inputType={GENERATOR_INPUT_TYPE.CHECKBOX}
          label={'isCommandResponse'}
          labelSx={{ minWidth: '170px', paddingLeft: '8px' }}
          name={`${name}.${index}.isCommandResponse`}
        />
      </Box>
      {isAnotherObjectWithRequestData && (
        <>
          <CommandParameters
            canChangeRefObject={canChangeRefObject}
            commandCode={watchedCommandCode?.id ?? ''}
            containerSx={{ mt: 1, mb: 1 }}
            fieldsObjectCode={objectCode}
            fieldsObjectTitle={objectTitle}
            jsScriptConfig={jsScriptConfig}
            label={t('label.commandParameters')}
            labelWidth={150}
            name={`${name}.${index}.parameters`}
            parametersMode='params'
            refObjectOptions={refObjectOptions}
            sourceCode={watchedSourceCommand?.id ?? ''}
            sourceTitle={watchedSourceCommand?.label ?? ''}
            typeSource={watchedSourceType?.id}
          />
          <CommandParameters
            canChangeRefObject={canChangeRefObject}
            commandCode={watchedCommandCode?.id ?? ''}
            fieldsObjectCode={objectCode}
            fieldsObjectTitle={objectTitle}
            jsScriptConfig={jsScriptConfig}
            label={t('label.commandBody')}
            labelWidth={150}
            name={`${name}.${index}.body`}
            parametersMode='body'
            refObjectOptions={refObjectOptions}
            sourceCode={watchedSourceCommand?.id ?? ''}
            sourceTitle={watchedSourceCommand?.label ?? ''}
            typeSource={watchedSourceType?.id}
          />
        </>
      )}
    </Fragment>
  )
}
